import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Body, Button} from '@lightricks/react-design-system';
import {assetThreadTarget} from '@/api/messages';
import {AssetMessageThread} from '@/api/messages/types';
import PoppaysLoader from '@/components/poppays-loader';
import useParams from '@/hooks/use-params';
import translate from '@/utils/translate';
import useGigShellData from './shell/useGigShellData';
import FilterChips from './components/FilterChips';
import SlotGroupHeader from './components/SlotGroupHeader';
import SlotRow from './components/SlotRow';
import PostRequirementRow from './components/PostRequirementRow';
import SubmissionSheet from './components/SubmissionSheet';
import MixedMediaEditor from './components/MixedMediaEditor';
import GigLoadError from './components/GigLoadError';
import GigShell from './shell/GigShell';
import {GigTab} from './shell/gigTabs';
import deliverableLabel from './deliverableLabel';
import {trackDeliverables, captureDeliverablesError} from './telemetry';
import {DeliverableSlot, SlotFilter, SlotState} from './types';
import styles from './GigDeliverablesPage.module.scss';

const FILTER_BY_SLOT_STATE: Record<SlotState, SlotFilter> = {
  empty: 'needs_action',
  edits_requested: 'needs_action',
  processing: 'in_review',
  pending: 'in_review',
  approved: 'approved',
  archived: 'archived',
};

export default function GigDeliverablesPage() {
  const {gigId} = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    deliverables,
    shell,
    posts,
    slots,
    totals: combined,
    isLoading,
    isError,
    error,
    gigFailed,
    submissionsFailed,
    refetch,
  } = useGigShellData(gigId);

  const {groups, isGrouped, archived, totalRequired, totalApproved} = slots;

  const [activeFilter, setActiveFilter] = useState<SlotFilter>('needs_action');
  const [sheetSlot, setSheetSlot] = useState<DeliverableSlot | null>(null);
  const [mmSlot, setMmSlot] = useState<DeliverableSlot | null>(null);
  const [mixedMediaDirty, setMixedMediaDirty] = useState(false);
  const openedAssetRequestRef = useRef<string | null>(null);
  const openedAssetSearchRef = useRef<string | null>(null);
  const approvedAssetRequestRef = useRef<string | null>(null);
  const allSlots = useMemo(
    () => groups.flatMap((group) => group.slots),
    [groups]
  );
  const archivedSlots = useMemo(
    () =>
      archived.map<DeliverableSlot>((submission, index) => ({
        key: `archived-${submission.id}`,
        deliverableKind: submission.deliverableKind,
        kind: submission.kind,
        index: index + 1,
        state: 'archived',
        isMixedMedia: submission.kind === 'mixed_media',
        submission,
      })),
    [archived]
  );
  const requestedSubmissionId = new URLSearchParams(location.search).get(
    'submissionId'
  );
  const requestedVersionId = new URLSearchParams(location.search).get(
    'versionId'
  );
  const requestedAssetId = new URLSearchParams(location.search).get('assetId');
  const requestedAssetKey = requestedSubmissionId
    ? [requestedSubmissionId, requestedAssetId, requestedVersionId].join(':')
    : null;

  useEffect(() => {
    trackDeliverables('Opened deliverables page', {gigId});
  }, [gigId]);

  useEffect(() => {
    if (isError) {
      captureDeliverablesError(error, 'Deliverables failed to load', {
        gigId,
        gigFailed,
        submissionsFailed,
      });
    }
  }, [isError, error, gigFailed, submissionsFailed, gigId]);

  const {replacedByGigId} = shell;
  useEffect(() => {
    if (replacedByGigId) {
      navigate(
        `${location.pathname.replace(gigId, replacedByGigId)}${
          location.search
        }`,
        {replace: true}
      );
    }
  }, [replacedByGigId, gigId, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!requestedSubmissionId) {
      if (
        openedAssetRequestRef.current &&
        mixedMediaDirty &&
        !window.confirm(translate('Discard your unsaved changes?'))
      ) {
        navigate(`${location.pathname}${openedAssetSearchRef.current ?? ''}`, {
          replace: true,
        });
        return;
      }
      if (openedAssetRequestRef.current) {
        setSheetSlot(null);
        setMmSlot(null);
      }
      openedAssetRequestRef.current = null;
      openedAssetSearchRef.current = null;
      return;
    }
    if (
      isLoading ||
      isError ||
      openedAssetRequestRef.current === requestedAssetKey
    )
      return;
    const requestedSlot = [...allSlots, ...archivedSlots].find(
      (slot) => slot.submission?.id === requestedSubmissionId
    );
    if (!requestedSlot) return;
    const changesMixedMediaSubmission =
      mixedMediaDirty &&
      mmSlot?.submission?.id !== requestedSlot.submission?.id;
    const navigationWasApproved =
      approvedAssetRequestRef.current === requestedAssetKey;
    approvedAssetRequestRef.current = null;
    if (
      changesMixedMediaSubmission &&
      !navigationWasApproved &&
      !window.confirm(translate('Discard your unsaved changes?'))
    ) {
      navigate(`${location.pathname}${openedAssetSearchRef.current ?? ''}`, {
        replace: true,
      });
      return;
    }
    openedAssetRequestRef.current = requestedAssetKey;
    openedAssetSearchRef.current = location.search;
    setActiveFilter(FILTER_BY_SLOT_STATE[requestedSlot.state]);
    if (requestedSlot.isMixedMedia) {
      setSheetSlot(null);
      setMmSlot(requestedSlot);
    } else {
      setMmSlot(null);
      setSheetSlot(requestedSlot);
    }
  }, [
    allSlots,
    archivedSlots,
    isError,
    isLoading,
    location.pathname,
    location.search,
    mixedMediaDirty,
    mmSlot?.submission?.id,
    navigate,
    requestedAssetKey,
    requestedSubmissionId,
  ]);

  const confirmAssetNavigation = (thread: AssetMessageThread) => {
    const target = assetThreadTarget(thread);
    if (
      !mixedMediaDirty ||
      !mmSlot?.submission ||
      target.submissionId === mmSlot.submission.id
    ) {
      return true;
    }
    if (!window.confirm(translate('Discard your unsaved changes?'))) {
      return false;
    }
    approvedAssetRequestRef.current = [
      target.submissionId,
      target.assetId,
      target.versionId,
    ].join(':');
    return true;
  };

  const closeAssetThread = () => {
    setSheetSlot(null);
    setMmSlot(null);
    const params = new URLSearchParams(location.search);
    params.delete('submissionId');
    params.delete('assetId');
    params.delete('versionId');
    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ''}`, {
      replace: true,
    });
  };

  const handleFilterChange = (filter: SlotFilter) => {
    setActiveFilter(filter);
    trackDeliverables('Changed deliverables filter', {gigId, filter});
  };

  const handleSelectKind = (tab: GigTab) => {
    if (tab.to) {
      navigate(tab.to);
    }
  };

  if (isLoading || replacedByGigId) {
    return <PoppaysLoader />;
  }

  if (isError) {
    return <GigLoadError onRetry={refetch} />;
  }

  const isInReview = (slot: DeliverableSlot) =>
    slot.state === 'processing' || slot.state === 'pending';
  const inReviewCount = allSlots.filter(isInReview).length;

  const chipCounts: Record<SlotFilter, number> = {
    needs_action: combined.needsAction,
    in_review: inReviewCount,
    approved: combined.done,
    all: combined.required,
    archived: archived.length,
  };

  function matchesFilter(slot: DeliverableSlot): boolean {
    switch (activeFilter) {
      case 'needs_action':
        return slot.state === 'empty' || slot.state === 'edits_requested';
      case 'in_review':
        return isInReview(slot);
      case 'approved':
        return slot.state === 'approved';
      case 'all':
        return true;
      default:
        return false;
    }
  }

  const handleSlotAction = (slot: DeliverableSlot) => {
    trackDeliverables('Opened deliverable slot', {
      gigId,
      kind: slot.kind,
      state: slot.state,
      index: slot.index,
      isMixedMedia: slot.isMixedMedia,
    });
    if (slot.isMixedMedia) {
      setMmSlot(slot);
    } else {
      setSheetSlot(slot);
    }
  };

  const filteredGroups = groups
    .map((g) => ({
      ...g,
      slots: g.slots.filter(matchesFilter),
    }))
    .filter((g) => g.slots.length > 0);

  const visiblePosts = posts.filter((p) => {
    if (activeFilter === 'needs_action') return !p.done;
    if (activeFilter === 'approved') return p.done;
    if (activeFilter === 'all') return true;
    return false;
  });

  const isFilterEmpty =
    filteredGroups.length === 0 && visiblePosts.length === 0;
  const showEmptyState = activeFilter !== 'archived' && isFilterEmpty;
  const emptyHeading =
    activeFilter === 'needs_action'
      ? 'You’re all caught up!'
      : 'Nothing to show here.';

  const hasUploadSlots = totalRequired > 0;
  const postsDone = posts.reduce((sum, p) => sum + p.doneCount, 0);
  const postsRequired = posts.reduce((sum, p) => sum + p.required, 0);
  const network = shell.socialNetworkName ?? 'Social';

  if (mmSlot) {
    return (
      <GigShell
        gigId={gigId}
        activeKind="deliverables"
        shell={shell}
        deliverables={deliverables}
        totals={combined}
        onSelectKind={handleSelectKind}
        onBeforeOpenAssetThread={confirmAssetNavigation}
      >
        <MixedMediaEditor
          key={mmSlot.submission?.id ?? mmSlot.key}
          gigId={gigId}
          slot={mmSlot}
          minCount={1}
          groupLabel={deliverableLabel(mmSlot.deliverableKind, mmSlot.kind)}
          readOnly={
            mmSlot.state === 'pending' ||
            mmSlot.state === 'approved' ||
            mmSlot.state === 'archived'
          }
          initialAssetId={requestedAssetId}
          initialVersionId={requestedVersionId}
          onDone={closeAssetThread}
          onDirtyChange={setMixedMediaDirty}
        />
      </GigShell>
    );
  }

  return (
    <GigShell
      gigId={gigId}
      activeKind="deliverables"
      shell={shell}
      deliverables={deliverables}
      totals={combined}
      onSelectKind={handleSelectKind}
    >
      <div className={styles.page}>
        <header className={styles.contentHead}>
          <h1 className={styles.title}>Deliverables</h1>
          <p className={styles.subtitle}>
            Complete every required deliverable for this gig — upload your
            assets and post to {network}.
          </p>
        </header>

        <FilterChips
          active={activeFilter}
          counts={chipCounts}
          onChange={handleFilterChange}
        />

        {activeFilter === 'archived' && (
          <div className={styles.archived} data-testid="archived-section">
            <Body size="sm" className={styles.archivedLabel}>
              Archived
            </Body>
            {archived.length === 0 ? (
              <Body size="sm">Nothing archived yet.</Body>
            ) : (
              archived.map((sub) => (
                <div
                  key={sub.id}
                  className={styles.archivedItem}
                  data-testid="archived-item"
                >
                  {sub.thumbnailUrl && (
                    <img
                      src={sub.thumbnailUrl}
                      alt=""
                      className={styles.archivedThumb}
                    />
                  )}
                  <Body size="xs">{sub.kind}</Body>
                </div>
              ))
            )}
          </div>
        )}
        {activeFilter !== 'archived' && showEmptyState && (
          <div className={styles.emptyState} data-testid="empty-state">
            <Body size="md">{emptyHeading}</Body>
            {activeFilter !== 'all' && (
              <Button
                testID="show-all-button"
                appearance="brand"
                mode="plain"
                size="medium"
                onClick={() => handleFilterChange('all')}
              >
                View all deliverables
              </Button>
            )}
          </div>
        )}
        {activeFilter !== 'archived' && !showEmptyState && (
          <div className={styles.slotList}>
            {hasUploadSlots && filteredGroups.length > 0 && (
              <section data-testid="section-upload-assets">
                <h2 className={styles.blockTitle}>
                  Upload assets{' '}
                  <span className={styles.blockWhere}>· DO IT HERE</span>
                </h2>
                <p className={styles.blockSub}>
                  {totalApproved} of {totalRequired} approved
                </p>
                {isGrouped ? (
                  filteredGroups.map((group) => (
                    <div
                      key={`${group.deliverableKind ?? ''}-${group.kind}`}
                      className={styles.group}
                    >
                      <SlotGroupHeader
                        label={group.label}
                        approvedCount={group.approvedCount}
                        requiredQuantity={group.requiredQuantity}
                      />
                      <div className={styles.slots}>
                        {group.slots.map((slot) => (
                          <SlotRow
                            key={slot.key}
                            slot={slot}
                            groupLabel={group.label}
                            onAction={handleSlotAction}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={styles.slots}>
                    {filteredGroups.flatMap((group) =>
                      group.slots.map((slot) => (
                        <SlotRow
                          key={slot.key}
                          slot={slot}
                          groupLabel={group.label}
                          onAction={handleSlotAction}
                        />
                      ))
                    )}
                  </div>
                )}
              </section>
            )}

            {visiblePosts.length > 0 && (
              <section data-testid="section-post-to-instagram">
                <h2 className={styles.blockTitle}>
                  Post to {network}{' '}
                  <span className={styles.blockWhere}>
                    · DO ON {network.toUpperCase()}
                  </span>
                </h2>
                <p className={styles.blockSub}>
                  {postsDone} of {postsRequired} done
                </p>
                {visiblePosts.map((p) => (
                  <PostRequirementRow key={p.key} req={p} />
                ))}
              </section>
            )}
          </div>
        )}

        {sheetSlot && (
          <SubmissionSheet
            open
            slot={sheetSlot}
            gigId={gigId}
            initialVersionId={requestedVersionId}
            onClose={closeAssetThread}
          />
        )}
      </div>
    </GigShell>
  );
}
